import { Drawer, Form, Spin, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EPanelOpenMethod } from '@components/block-event-single/store';
import { ICustomDimensionDetailPanel } from '@components/detail-panel/components/CustomDimensionDetailPanel/typed';
import CustomDimensionInfoForm from '@components/detail-panel/components/CustomDimensionInfoForm';
import { EventTitle } from '@components/detail-panel/components/EventInfoCard/styled';
import MappingRulesExplorer from '@components/detail-panel/components/EventMappingRules/MappingRulesExplorer';
import MappedEventRelatedCard from '@components/detail-panel/components/MappedEventRelatedCard';
import ToolBar from '@components/detail-panel/components/ToolBar';
import { DrawerMainLeft, DrawerMainRight } from '@components/detail-panel/styled';
import { useDimensionWorkflow, useTopPanelStore } from '@hooks';
import { EEventManagementTabsType } from '@src/common/constant/enum';
import {
  useCreateCustomDimension,
  useDeleteCustomDimension,
  useUpdateCustomDimension,
} from '@src/emp-3.0/api/queryHooks';
import CustomDimensionRuleBuilder from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/components/RuleBuilder';
import { useCustomDimensionPanelStore } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/store';
import {
  generateCustomDimensionPayload,
  generateSelectEventType,
  isGlobalAttributeType,
  isNetworkCategory,
  trimCustomDimensionPayloadMappedEventNames,
  validateCustomDimensionPayload,
} from '@src/emp-3.0/components/custom-dimension/utils';
import { validateDimensionName } from '@src/emp-3.0/domain/dimension';
import {
  ECategoryType,
  EDraftStatus,
  ICreateCustomDimensionPayload,
  IUpdateCustomDimensionPayload,
} from '@src/emp-3.0/types';
import { trackViewinSemanticClick } from '@src/sensor';

import EventStatusCard from '../EventStatusCard';
import {
  handleError,
  handleSuccess,
} from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { EFormMode } from '@src/emp-3.0/types/common';
import { useCustomDimensionStore } from 'emp-3.0/components/Dimensions/store';
import { ALL_APPLICABLE_MAPPED_EVENTS_TYPE } from '@src/emp-3.0/constants/dimensions';
const CustomDimensionDetailPanel: React.FC<ICustomDimensionDetailPanel> = ({
  id,
  data,
  open,
  onClose,
  fixedWidth = 300,
  operationMode = EPanelOpenMethod.VIEW,
  setOperationMode,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNewWorkflow = useDimensionWorkflow();
  const { setFormMode: setWorkflowFormMode } = useWorkflowStore();
  const { resetCustomDimensionStore } = useCustomDimensionStore((state) => state);
  const { mutate: createCustomDimension, isLoading: isCreateCustomDimensionLoading } =
    useCreateCustomDimension();
  const { mutate: updateCustomDimension, isLoading: isUpdateCustomDimensionLoading } =
    useUpdateCustomDimension();
  const { mutate: deleteCustomDimension, isLoading: isDeleteCustomDimensionLoading } =
    useDeleteCustomDimension();

  const { openCustomDimensionPanel } = useTopPanelStore();
  const { selectEventType, setCustomDimensionPayload } = useCustomDimensionPanelStore(
    (state) => state,
  );

  const [customDimensionPayloadDraft, setCustomDimensionPayloadDraft] = useState<
    IUpdateCustomDimensionPayload | ICreateCustomDimensionPayload | undefined
  >();
  const [selectEventTypeDraft, setSelectEventTypeDraft] = useState<string>(selectEventType);

  const isViewMode = useMemo(() => operationMode === EPanelOpenMethod.VIEW, [operationMode]);
  const isReadOnly = useMemo(
    () => !data?.is_latest && data?.is_draft === EDraftStatus.notDraft,
    [data],
  );

  const initCustomDimensionPayloadDraft = useCallback(() => {
    const { attribute_type, mapped_event_names } = data;
    setCustomDimensionPayloadDraft(data);
    const newSelectEventType: string = generateSelectEventType(attribute_type, mapped_event_names);
    setSelectEventTypeDraft(newSelectEventType);
  }, [data, id]);

  useEffect(() => {
    if (data) {
      initCustomDimensionPayloadDraft();
    } else {
      setCustomDimensionPayloadDraft(undefined);
    }
  }, [data, initCustomDimensionPayloadDraft, operationMode]);

  useEffect(() => {
    const fieldsValue = {
      display_name: customDimensionPayloadDraft?.display_name,
      category: customDimensionPayloadDraft?.category,
    };
    form.setFieldsValue(fieldsValue);
  }, [customDimensionPayloadDraft, form]);

  useEffect(() => {
    if (operationMode === EPanelOpenMethod.CLONE) {
      setCustomDimensionPayloadDraft({
        ...data,
        display_name: data?.display_name + '_Copy',
      });
    } else {
      initCustomDimensionPayloadDraft();
    }
  }, [operationMode]);

  const handleEditClick = () => {
    if (isNewWorkflow) {
      navigate(`/workflow/dimension/${id}/eventLooker`);
      return;
    }
    setOperationMode(EPanelOpenMethod.EDIT);
  };

  const handleCloneClick = () => {
    if (isNewWorkflow) {
      resetCustomDimensionStore();
      setWorkflowFormMode(EFormMode.CLONE);
      navigate(`/workflow/dimension/${id}`);
      return;
    }
    setOperationMode(EPanelOpenMethod.CLONE);
  };

  const handleCloseClick = () => {
    onClose?.();
  };

  const handleCancelClick = () => {
    setOperationMode(EPanelOpenMethod.VIEW);
    initCustomDimensionPayloadDraft();
  };

  const handleSaveClick = () => {
    const isAllEvent = selectEventTypeDraft === ALL_APPLICABLE_MAPPED_EVENTS_TYPE;
    const bypassFields = isAllEvent ? ['mapped_event_names'] : [];
    const { failed: validatePayloadFailed, message: validatePayloadMessage } =
      validateCustomDimensionPayload(customDimensionPayloadDraft, bypassFields);
    if (validatePayloadFailed) {
      message.warning(validatePayloadMessage);
      return;
    }
    const { failed: validateNameFailed, message: validateNameMessage } = validateDimensionName(
      customDimensionPayloadDraft?.display_name,
    );
    if (validateNameFailed) {
      message.warning(validateNameMessage);
      return;
    }
    const payload = (
      isAllEvent
        ? { ...generateCustomDimensionPayload(customDimensionPayloadDraft), mapped_event_names: [] }
        : trimCustomDimensionPayloadMappedEventNames(
            generateCustomDimensionPayload(customDimensionPayloadDraft),
          )
    ) as IUpdateCustomDimensionPayload;

    if (operationMode === EPanelOpenMethod.EDIT) {
      updateCustomDimension(
        {
          custom_dimension_id: payload.custom_dimension_id,
          queryBody: payload,
        },
        {
          onSuccess: () => {
            handleSuccess('edit');
            handleCloseClick();
          },
          onError: handleError('edit'),
        },
      );
      return;
    }
    createCustomDimension(
      { queryBody: payload },
      {
        onSuccess: () => {
          handleSuccess('create');
          handleCloseClick();
        },
        onError: handleError('create'),
      },
    );
  };

  const handleCloneCustomDimension = () => {
    handleSaveClick();
  };

  const handleDeleteClick = () => {
    if (customDimensionPayloadDraft) {
      deleteCustomDimension(
        (customDimensionPayloadDraft as IUpdateCustomDimensionPayload).custom_dimension_id,
        {
          onSuccess: () => {
            handleCloseClick();
            message.success('Dimension Deleted Successfully!');
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
            message.error(errorMessage);
          },
        },
      );
    }
  };

  const handleChange = (value: { [key: string]: string | string[] | number }) => {
    if (customDimensionPayloadDraft) {
      let selectEventType = selectEventTypeDraft;
      setCustomDimensionPayloadDraft((prevState) => {
        if (prevState) {
          const { attribute_type, mapped_event_names, category } = prevState;
          const mappedEventNames = (
            value.attribute_name ? [] : value.mapped_event_names || mapped_event_names
          ) as string[];
          const attributeType = (value.attribute_type || attribute_type) as number;
          selectEventType =
            value.mapped_event_names || value.attribute_type
              ? generateSelectEventType(attributeType, mappedEventNames)
              : selectEventTypeDraft;

          // this is not needed. Regression is required to test this.
          // let updatedCategory;
          // if(value.is_promoted_as_global === 1) {
          //   updatedCategory = ECategoryType.GLOBAL_TAGS;
          // } else if (isNetworkCategory(category)) {
          //   updatedCategory = category;
          // } else if (isGlobalAttributeType(attributeType)) {
          //   updatedCategory = ECategoryType.GLOBAL_TAGS;
          // } else {
          //   updatedCategory = ECategoryType.EVENT_TAGS;
          // }

          return {
            ...prevState,
            ...value,
            // category: updatedCategory,
            mapped_event_names: mappedEventNames,
          };
        } else {
          return;
        }
      });
      setSelectEventTypeDraft((prevState) => {
        return prevState !== selectEventType ? selectEventType : prevState;
      });
    }
  };

  const handleClickViewButton = () => {
    if (customDimensionPayloadDraft) {
      trackViewinSemanticClick({ type: 'Custom Dimension', ...customDimensionPayloadDraft });
      openCustomDimensionPanel();
      setCustomDimensionPayload({ ...customDimensionPayloadDraft });
      navigate(`/semantic-mapper`);
    }
  };

  return (
    <Drawer
      open={open}
      closable={false}
      mask={false}
      bodyStyle={{ padding: 0 }}
      getContainer={false}
      destroyOnClose
      forceRender
      width={`calc(100% - ${fixedWidth}px)`}
    >
      <Spin
        spinning={
          isUpdateCustomDimensionLoading ||
          isDeleteCustomDimensionLoading ||
          isCreateCustomDimensionLoading
        }
      >
        <ToolBar
          mode={operationMode}
          data={data}
          type={EEventManagementTabsType.CUSTOM_DIMENSION}
          onClose={handleCloseClick}
          onEdit={handleEditClick}
          onCancel={handleCancelClick}
          onSave={handleSaveClick}
          onDelete={handleDeleteClick}
          onClone={handleCloneClick}
          onSaveAs={handleCloneCustomDimension}
        />
        <div style={{ display: 'flex', overflow: 'scroll', height: '100%', minHeight: '600px' }}>
          <DrawerMainLeft>
            <EventTitle>Details of “{customDimensionPayloadDraft?.display_name}”</EventTitle>
            <CustomDimensionInfoForm
              mode={operationMode}
              form={form}
              disabled={isViewMode}
              onValuesChange={(changedValues, values) => {
                setCustomDimensionPayloadDraft({ ...customDimensionPayloadDraft, ...values });
              }}
            />
            <div style={{ flex: 1 }}>
              {isNewWorkflow ? null : (
                <MappingRulesExplorer
                  readOnly={isReadOnly}
                  mode={operationMode}
                  title="Mapping Rules"
                  buttonText="View in Semantic Mapper"
                  onClick={handleClickViewButton}
                />
              )}
              <CustomDimensionRuleBuilder
                disabled={isViewMode}
                eventTag={customDimensionPayloadDraft?.attribute_name}
                eventNames={customDimensionPayloadDraft?.mapped_event_names}
                attributeType={customDimensionPayloadDraft?.attribute_type}
                selectEventType={selectEventTypeDraft}
                promoteAsGlobal={customDimensionPayloadDraft?.is_promoted_as_global}
                dimensionIdentifier={customDimensionPayloadDraft?.dimension_identifier}
                onEventTagChange={(value) => handleChange({ attribute_name: value })}
                onEventNamesChange={(value) => handleChange({ mapped_event_names: value })}
                onAttributeTypeChange={(value) => handleChange({ attribute_type: value })}
                onSelectEventTypeChange={(value) => setSelectEventTypeDraft(value)}
                onCategoryChange={(value) => handleChange({ category: value })}
                onPromoteAsGlobalChange={(value) => handleChange({ is_promoted_as_global: value })}
              />
            </div>
          </DrawerMainLeft>
          {operationMode !== EPanelOpenMethod.CLONE && (
            <DrawerMainRight>
              <EventStatusCard
                type={EEventManagementTabsType.CUSTOM_DIMENSION}
                customDimension={data}
              />
              <MappedEventRelatedCard dimension={data} />
            </DrawerMainRight>
          )}
        </div>
      </Spin>
    </Drawer>
  );
};

export default CustomDimensionDetailPanel;
